/**
 * DefaultVariant — Cart Summary V2
 *
 * Renders the standard cart summary layout:
 *   - Subtotal row: label + compare-at (strikethrough) + actual total
 *   - Savings row: green branded "YOU'RE SAVING" with amount
 *   - Error message (when present)
 *   - Full-width checkout CTA
 */

'use client';

import { formatPrice } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';
import {
  SummaryContainer,
  Row,
  RowPrices,
  SubtotalLabel,
  CompareAtPrice,
  TotalPrice,
  SavingsRow,
  SavingsLabel,
  SavingsValue,
  SavingsIcon,
  CheckoutButton,
  CheckoutButtonContent,
  ErrorMessage,
} from '../CartSummaryV2.styled';
import type { VariantProps } from '../types';

const BookmarkIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" />
  </svg>
);

const LockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z" />
  </svg>
);

export function DefaultVariant({
  total,
  compareAtTotal,
  savings,
  onCheckout,
  isCheckoutDisabled,
  error,
  priceOptions,
  offerFooter,
  menuFooter,
}: Readonly<VariantProps>) {
  const { t } = useTranslation('sidecart');

  const showCompareAt = compareAtTotal != null && compareAtTotal > total;

  return (
    <>
      {offerFooter}

      <SummaryContainer>
        <Row>
          <SubtotalLabel variant="body" weight="regular">
            {t('cart.subtotal')}
          </SubtotalLabel>
          <RowPrices>
            {showCompareAt && (
              <CompareAtPrice variant="body" weight="regular" data-testid="compare-at-price">
                {formatPrice(compareAtTotal, priceOptions)}
              </CompareAtPrice>
            )}
            <TotalPrice variant="body" weight="bold" data-testid="subtotal-price">
              {formatPrice(total, priceOptions)}
            </TotalPrice>
          </RowPrices>
        </Row>

        {savings > 0 && (
          <SavingsRow>
            <SavingsLabel variant="body" weight="regular">
              <SavingsIcon>
                <BookmarkIcon />
              </SavingsIcon>
              {t('cart.savingsLabel')}
            </SavingsLabel>
            <SavingsValue variant="body" weight="bold" data-testid="savings-price">
              {formatPrice(savings, priceOptions)}
            </SavingsValue>
          </SavingsRow>
        )}
      </SummaryContainer>

      {error && (
        <ErrorMessage variant="body" weight="regular">
          {String(error)}
        </ErrorMessage>
      )}

      <CheckoutButton
        disabled={isCheckoutDisabled}
        onClick={onCheckout}
        text={
          <CheckoutButtonContent>
            <LockIcon />
            <span>{t('cart.checkout')}</span>
          </CheckoutButtonContent>
        }
        wide
        testId="checkout"
      />

      {menuFooter}
    </>
  );
}
